import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  flexPropNames,
  flexTypes,
  flexClassnames
} from '@loggi/ui/components/utils/flex';
import colors from '@loggi/ui/components/utils/colors';
import { gutterPropMeasure } from '@loggi/ui/components/utils/gutter';
import pick from 'lodash/pick';
import omit from 'lodash/omit';

export default class View extends React.Component {
  static displayName = 'View';

  static propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    inline: PropTypes.bool,
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.node
    ]),
    backgroundColor: PropTypes.oneOf(colors.backgroundHexKeys),
    gutter: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

    /*
     * The following props are flex properties.
     * To know the real possible values, go to /scss/variables/_flex.scss
     * and look for the map keys
     */
    alignContent: PropTypes.oneOf(flexTypes.alignContent),
    alignItems: PropTypes.oneOf(flexTypes.alignItems),
    alignSelf: PropTypes.oneOf(flexTypes.alignSelf),
    flexDirection: PropTypes.oneOfType([
      PropTypes.shape({
        xs: PropTypes.oneOf(flexTypes.flexDirection),
        sm: PropTypes.oneOf(flexTypes.flexDirection),
        md: PropTypes.oneOf(flexTypes.flexDirection),
        lg: PropTypes.oneOf(flexTypes.flexDirection),
        xl: PropTypes.oneOf(flexTypes.flexDirection),
        xxl: PropTypes.oneOf(flexTypes.flexDirection)
      }),
      PropTypes.oneOf(flexTypes.flexDirection)
    ]),
    flexGrow: PropTypes.oneOf(flexTypes.flexGrow),
    flexShrink: PropTypes.oneOf(flexTypes.flexShrink),
    flexWrap: PropTypes.oneOf(flexTypes.flexWrap),
    justifyContent: PropTypes.oneOf(flexTypes.justifyContent)
  };

  static defaultProps = {
    inline: false,
    gutter: null
  };

  render() {
    const {
      children,
      inline,
      backgroundColor,
      gutter,
      ...otherProps
    } = this.props;

    const flexProps = pick(otherProps, flexPropNames);
    const remainingProps = omit(otherProps, flexPropNames);

    const gutters = gutterPropMeasure(gutter);

    const elementClasses = classNames([
      otherProps.className,
      { [`background-${backgroundColor}`]: backgroundColor },
      { [`gutter-xs-top-${gutters.xs.top}`]: !!gutters.xs.top },
      { [`gutter-sm-top-${gutters.sm.top}`]: !!gutters.sm.top },
      { [`gutter-md-top-${gutters.md.top}`]: !!gutters.md.top },
      { [`gutter-lg-top-${gutters.lg.top}`]: !!gutters.lg.top },
      { [`gutter-xl-top-${gutters.xl.top}`]: !!gutters.xl.top },
      { [`gutter-xxl-top-${gutters.xxl.top}`]: !!gutters.xxl.top },
      { [`gutter-xs-bottom-${gutters.xs.bottom}`]: !!gutters.xs.bottom },
      { [`gutter-sm-bottom-${gutters.sm.bottom}`]: !!gutters.sm.bottom },
      { [`gutter-md-bottom-${gutters.md.bottom}`]: !!gutters.md.bottom },
      { [`gutter-lg-bottom-${gutters.lg.bottom}`]: !!gutters.lg.bottom },
      { [`gutter-xl-bottom-${gutters.xl.bottom}`]: !!gutters.xl.bottom },
      { [`gutter-xxl-bottom-${gutters.xxl.bottom}`]: !!gutters.xxl.bottom },
      { [`gutter-xs-left-${gutters.xs.left}`]: !!gutters.xs.left },
      { [`gutter-sm-left-${gutters.sm.left}`]: !!gutters.sm.left },
      { [`gutter-md-left-${gutters.md.left}`]: !!gutters.md.left },
      { [`gutter-lg-left-${gutters.lg.left}`]: !!gutters.lg.left },
      { [`gutter-xl-left-${gutters.xl.left}`]: !!gutters.xl.left },
      { [`gutter-xxl-left-${gutters.xxl.left}`]: !!gutters.xxl.left },
      { [`gutter-xs-right-${gutters.xs.right}`]: !!gutters.xs.right },
      { [`gutter-sm-right-${gutters.sm.right}`]: !!gutters.sm.right },
      { [`gutter-md-right-${gutters.md.right}`]: !!gutters.md.right },
      { [`gutter-lg-right-${gutters.lg.right}`]: !!gutters.lg.right },
      { [`gutter-xl-right-${gutters.xl.right}`]: !!gutters.xl.right },
      { [`gutter-xxl-right-${gutters.xxl.right}`]: !!gutters.xxl.right },
      {
        [`display-flex`]: !!(
          flexProps.justifyContent ||
          flexProps.flexDirection ||
          flexProps.flexWrap ||
          flexProps.alignItems ||
          flexProps.alignContent
        )
      },
      ...flexClassnames(flexProps)
    ]);

    const allProps = elementClasses
      ? Object.assign({}, remainingProps, { className: elementClasses })
      : remainingProps;

    if (inline) {
      return <span {...allProps}>{children}</span>;
    }

    return <div {...allProps}>{children}</div>;
  }
}
